import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PROJECT_PORTAL_VERSION, SITE_APP_VERSION } from "../../config/branding";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          password: password,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Login failed");
      }

      const data = await response.json();

      if (data.access_token) {
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("userRole", data.roles);
        localStorage.setItem("userEmail", data.email);
        localStorage.setItem("userId", data.userId);
        localStorage.setItem("userName", data.name || "");
        localStorage.setItem("usesDefaultPassword", String(Boolean(data.usesDefaultPassword)));

        // 确保正确保存allowedProjects到localStorage
        if (data.allowedProjects) {
          const projects = Array.isArray(data.allowedProjects)
            ? data.allowedProjects
            : [];
          localStorage.setItem("allowedProjects", JSON.stringify(projects));
        } else if (data.user && data.user.allowedProjects) {
          const projects = Array.isArray(data.user.allowedProjects)
            ? data.user.allowedProjects
            : [];
          localStorage.setItem("allowedProjects", JSON.stringify(projects));
        } else {
          localStorage.setItem("allowedProjects", JSON.stringify([]));
        }

        // 从localStorage中获取已存储的userRole
        const storedUserRole = localStorage.getItem("userRole");

        if (storedUserRole === "trade") {
          navigate("/trade/projects");
        } else if (storedUserRole === "site") {
          navigate("/site_select_project");
        } else {
          navigate("/app_home");
        }
      } else {
        setError("Invalid server response");
      }
    } catch (error) {
      setError(error.message || "Server connection error");
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="w-full md:w-1/2 h-[30vh] md:h-screen relative">
        <img
          src="/images/condo.webp"
          alt="Condo"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>

      <div
        className="w-full md:w-1/2 flex items-center justify-center p-6 md:p-12"
        style={{ backgroundColor: "#F5F3F0" }}
      >
        <div className="w-full max-w-[480px]">
          <div className="mb-6 md:mb-12 flex justify-center md:justify-end">
            <img
              src="/images/gemterra-logo-20260112-t.png"
              alt="Gemterra Logo"
              className="h-10 md:h-14"
            />
          </div>

          <div className="flex flex-col md:flex-row md:items-baseline md:justify-between mb-4 md:mb-8">
            <h1
              className="text-xl md:text-3xl font-bold text-center md:text-left"
              style={{ color: "#333333" }}
            >
              Project Management Portal
            </h1>
            <h3
              className="text-xs md:text-sm font-inter text-center md:text-right mt-1 md:mt-0"
              style={{ color: "#A89F91" }}
            >
              {PROJECT_PORTAL_VERSION}
            </h3>
          </div>

          <form onSubmit={handleLogin} className="space-y-4 md:space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Name
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 md:px-4 py-2 md:py-3 bg-white focus:outline-none"
                style={{ borderRadius: "6px", border: "1px solid #A89F91" }}
                onFocus={(e) => (e.target.style.border = "2px solid #224228")}
                onBlur={(e) => (e.target.style.border = "1px solid #A89F91")}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 md:py-3 pr-10 bg-white focus:outline-none"
                  style={{ borderRadius: "6px", border: "1px solid #A89F91" }}
                  onFocus={(e) => (e.target.style.border = "2px solid #224228")}
                  onBlur={(e) => (e.target.style.border = "1px solid #A89F91")}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && <div className="text-red-500 text-sm">{error}</div>}

            <button
              type="submit"
              className="w-full px-4 text-white transition duration-200 font-medium"
              style={{
                backgroundColor: "#224228",
                height: "48px",
                borderRadius: "6px",
                letterSpacing: "0.5px",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#1a3320")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#224228")}
            >
              Login
            </button>
          </form>

          {/* 应用说明和下载区域（弱化处理，响应式布局） */}
          <div className="mt-6 md:mt-8 mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row items-center md:items-start md:justify-between space-y-4 md:space-y-0 md:space-x-6">
              {/* 左侧：应用说明文字 */}
              <div className="flex-1 text-center md:text-left">
                <p
                  className="font-medium mb-1 md:mb-2"
                  style={{ fontSize: "11px", color: "#A89F91" }}
                >
                  Gemterra Site App ({SITE_APP_VERSION}) is now available.
                </p>
                <p
                  className="leading-relaxed hidden md:block"
                  style={{ fontSize: "11px", color: "#A89F91" }}
                >
                  This iOS app helps site teams share updates, provide feedback,
                  and access construction documents in real time.
                </p>
              </div>

              {/* 右侧：App Store下载和二维码 */}
              <div className="flex flex-row md:flex-col items-center space-x-4 md:space-x-0 md:space-y-2 flex-shrink-0">
                {/* Apple Store下载链接 */}
                <div>
                  <a
                    href="https://apps.apple.com/ca/app/gemterrasiteapp/id6747144702"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img
                      src="/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                      alt="Download on the App Store"
                      className="h-5 md:h-6 w-auto hover:opacity-80 transition-opacity duration-200"
                    />
                  </a>
                </div>

                {/* 二维码 */}
                <div>
                  <img
                    src="/images/GemterraSiteAppQRcode.jpg"
                    alt="QR Code"
                    className="w-[40px] h-[40px] md:w-[50px] md:h-[50px] object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-4 md:mt-6">
            <a
              href="/privacy"
              className="text-xs hover:underline transition duration-200"
              style={{ color: "#A89F91" }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
